import { useCallback } from 'react';
import { usePermissions, UserPermission, WriteOn, ReadOn } from 'modules/permissions';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import { OpportunityDisplayEnum } from '../../../enums/opportunity-display.enum';
import { Opportunity } from '../../../queries/view-opportunity.queries.types';
import {
   StyledStatusBarUpdatedBy,
   StyledSubHeader,
   StyledSubHeaderControl,
   StyledSubHeaderControlContainer,
   StyledSubHeaderGroup,
   StyledSubHeaderLabel,
   StyledSubHeaderSection,
   StyledSubHeaderTabContainer
} from 'shared/styled/SubHeader.styled';
import SubHeaderTab from 'components/SubHeaderTab';
import { useForm } from 'modules/forms';
import { OpportunityEntity } from 'shared/types/domain/OpportunityEntity';
import DateTimeInput from 'components/controls/DateTimeInput';
import { FontIcon } from '@fluentui/react';
import { DateTime } from 'luxon';

interface OpportunitySubHeaderProps {
   opportunity: Opportunity | undefined;
   displayMode: OpportunityDisplayEnum;
   formHasErrors: boolean;
   goToOverview: () => void;
   goToCompletionDetails: () => void;
   goToAuditLog: () => void;
}

const OpportunitySubHeader = ({
   opportunity,
   displayMode,
   formHasErrors,
   goToOverview,
   goToCompletionDetails,
   goToAuditLog
}: OpportunitySubHeaderProps) => {
   const { updateFields, formErrors } = useForm<OpportunityEntity>();
   const { hasPermission } = usePermissions();

   const formatDate = (date?: Date): string => {
      if (!date) return '';

      const dt = DateTime.fromJSDate(date);
      return dt.toLocaleString({...DateTime.DATE_SHORT, weekday: 'short', month: 'short'});
   };


   const lastUpdateText = (): string => {
      if (opportunity) {
         return `Last Updated On ${formatDate(new Date(opportunity.lastModified))}`;
      }

      return "";
   };

   const allowShowCompletionDetails = useCallback(() => {
      return opportunity?.completion !== undefined;
   }, [opportunity]);

   const handleIncidentDateChange = (incidentDate: string | null | undefined) => {
      updateFields({
         incidentDate: { runValidation: true, value: incidentDate }
      });
   };

   return (
      <StyledSubHeader>
         <StyledSubHeaderSection style={{ maxWidth: '25%' }}>
            <StyledSubHeaderGroup>
               <StyledSubHeaderControlContainer>
                  <StyledSubHeaderLabel>Incident Date</StyledSubHeaderLabel>
                  <StyledSubHeaderControl>
                     <ConditionalRender condition={hasPermission(WriteOn(UserPermission.IncidentDate))}>
                        <DateTimeInput
                           value={opportunity?.incidentDate!.toString()}
                           onDateTimeSelect={handleIncidentDateChange}
                           disableFuture={true}
                           errorMessage={formErrors?.incidentDate}
                        />
                     </ConditionalRender>
                     <ConditionalRender condition={!hasPermission(WriteOn(UserPermission.IncidentDate))}>
                        {opportunity?.incidentDate.toString()}
                     </ConditionalRender>
                  </StyledSubHeaderControl>
               </StyledSubHeaderControlContainer>
            </StyledSubHeaderGroup>
            <StyledSubHeaderGroup>
               <StyledSubHeaderControlContainer>
                  <StyledSubHeaderLabel>Status</StyledSubHeaderLabel>
                  <StyledSubHeaderControl>
                     {`${opportunity?.observationStatus.toProperCase()}`}
                  </StyledSubHeaderControl>
               </StyledSubHeaderControlContainer>
            </StyledSubHeaderGroup>
         </StyledSubHeaderSection>

         <StyledSubHeaderSection style={{ flex: '2 1 auto' }}>
            <StyledSubHeaderGroup style={{ justifyContent: 'flex-end' }}>
               <StyledStatusBarUpdatedBy>{lastUpdateText()}</StyledStatusBarUpdatedBy>
            </StyledSubHeaderGroup>

            <StyledSubHeaderGroup style={{ justifyContent: 'flex-end' }}>
               <StyledSubHeaderTabContainer>
                  <SubHeaderTab
                     onClick={goToOverview}
                     isActive={displayMode === OpportunityDisplayEnum.Overview}
                     display={'Overview'}
                     highlightErrors={formHasErrors}
                  />
                  <ConditionalRender condition={allowShowCompletionDetails}>
                     <SubHeaderTab
                        onClick={goToCompletionDetails}
                        isActive={displayMode === OpportunityDisplayEnum.Completion}
                        display={'Completion'}
                     />
                  </ConditionalRender>
                  <ConditionalRender condition={hasPermission(ReadOn(UserPermission.Audit))}>
                     <SubHeaderTab
                        onClick={goToAuditLog}
                        isActive={displayMode === OpportunityDisplayEnum.Audit}
                        display={<FontIcon iconName='History' />}
                        title={'Audit Log'}
                     />
                  </ConditionalRender>
               </StyledSubHeaderTabContainer>
            </StyledSubHeaderGroup>
         </StyledSubHeaderSection>
      </StyledSubHeader>
   );
};

export default OpportunitySubHeader;
